// Lightweight service worker registration for dashboard (Vite)
// Registers /service-worker.js in production to enable offline caching.
//
// Updates are the coach's call (client 2026-10-07, in-game parity): a new
// build installs and WAITS; this raises the "update waiting" flag (red dot on
// the sigma, "Update Available!" prompt) instead of swapping builds under the
// coach. Taking it — the prompt or Check For Updates — activates the waiting
// worker, and the page reloads onto it.
import { forgetUpdatePromptDismissed, hasWaitingWorker, markUpdatePending } from "@workspace/ui";

/** How often to look for a new build while the app stays open. */
const UPDATE_PROBE_MS = 15 * 60 * 1000;

const isLocalhost: boolean = Boolean(
  window.location.hostname === "localhost" ||
  window.location.hostname === "[::1]" ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  // A new worker takes control only when the coach takes the update — and it
  // claims EVERY open page, not just the one that asked. Each page must then
  // reload: its JavaScript is the old build, and navigation is served
  // cache-first from the new worker's cache. Only attach when a controller
  // ALREADY exists — i.e. this is an update, not the first install — so the
  // very first visit doesn't reload on itself. `refreshing` keeps the page
  // that asked (which reloads itself too) to one navigation.
  if (navigator.serviceWorker.controller) {
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  }

  const baseUrl = import.meta.env.BASE_URL || "/";
  const swUrl = new URL("service-worker.js", `${window.location.origin}${baseUrl}`).href;

  // Register immediately – don't wait for window.load
  // This ensures the SW is active ASAP so assets get cached
  // even if the user installs the PWA before logging in.
  if (isLocalhost) {
    checkValidServiceWorker(swUrl);
    navigator.serviceWorker.ready.catch(() => {});
  } else {
    registerValidSW(swUrl);
  }
}

function registerValidSW(swUrl: string): void {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.info("[dashboard] SW registered", registration.scope);
      // An update found in an EARLIER session and never taken is still
      // waiting in the registration: bring the dots back for it.
      if (hasWaitingWorker(registration)) markUpdatePending();
      else forgetUpdatePromptDismissed();
      // An install already under way at startup, and every one found later.
      if (registration.installing) watchInstall(registration.installing);
      registration.onupdatefound = () => {
        if (registration.installing) watchInstall(registration.installing);
      };
      probeForUpdates(registration);
    })
    .catch((error) => {
      console.error("[dashboard] SW registration failed", error);
    });
}

/**
 * A new build finished installing and is WAITING. Don't take it here — that
 * reloaded the page on its own, mid-practice if that is when it landed.
 * Raise the flag; the coach takes it when they choose.
 */
function watchInstall(worker: ServiceWorker): void {
  worker.addEventListener("statechange", () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      console.info("[dashboard] New version available — waiting for the coach to update");
      markUpdatePending();
    }
  });
}

/**
 * Browsers (installed PWAs especially) rarely re-check the service worker on
 * their own, so a new build would wait for days to be found. Probe when the
 * app comes back to the front, when the connection returns, and every 15
 * minutes — an update found installs quietly and raises the flag.
 */
function probeForUpdates(registration: ServiceWorkerRegistration): void {
  const probe = () => {
    registration.update().catch(() => {
      /* offline or blocked — the next probe tries again */
    });
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") probe();
  });
  window.addEventListener("online", probe);
  window.setInterval(probe, UPDATE_PROBE_MS);
}

function checkValidServiceWorker(swUrl: string): void {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (response.status === 404 || (contentType && !contentType.includes("javascript"))) {
        navigator.serviceWorker.ready
          .then((registration) => registration.unregister())
          .catch(() => {})
          .finally(() => window.location.reload());
      } else {
        registerValidSW(swUrl);
      }
    })
    .catch(() => {
      console.info("[dashboard] Offline mode; using cached content when available");
    });
}

export function unregister(): void {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error("[dashboard] SW unregister failed", error));
  }
}
